import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import SearchableSelect from 'shared/components/SearchableSelect';
import { toast } from 'react-hot-toast';
import { fetchActiveTenantUsers } from 'shared/api/services/tenantUserService';
import { getRegistryTenantIdFromSession } from 'shared/utils/sessionProfileStore';
import { listStatusParams, riskRegisterApi } from '../api/systemRegisterApi';
import {
  SOFTWARE_RISK_CATEGORY,
  SOFTWARE_RISK_PRIORITY,
  SOFTWARE_RISK_STATUS,
  formatEnumLabel,
} from '../constants/enums';
import { apiErrorMessage, unwrapList } from '../utils/responseHelpers';
import FormSection from './FormSection';

export const emptySoftwareRiskRow = () => ({
  key: `risk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  id: null,
  riskRegisterId: null,
  riskCategory: '',
  resourcesImpacted: '',
  riskName: '',
  riskPriority: '',
  riskDescription: '',
  riskOwner: '',
  impact: '',
  causes: '',
  controlsAndMitigation: '',
  status: 'OPEN',
});

const userFilter = createFilterOptions({
  stringify: (option) =>
    `${option.name ?? ''} ${option.email ?? ''} ${option.id ?? ''}`.toLowerCase(),
});

const riskRegisterFilter = createFilterOptions({
  stringify: (option) =>
    `${option.riskName ?? ''} ${option.riskCategory ?? ''} ${option.riskOwner ?? ''} ${option.id ?? ''}`.toLowerCase(),
});

const userLabel = (u) => {
  if (!u) return '';
  const name = (u.name || '').trim();
  const email = (u.email || '').trim();
  if (name && email) return `${name} (${email})`;
  return name || email || '';
};

const riskRegisterLabel = (r) => {
  if (!r) return '';
  const name = (r.riskName || '').trim() || `Risk #${r.id}`;
  const category = r.riskCategory ? formatEnumLabel(r.riskCategory) : '';
  const priority = r.riskPriority ? formatEnumLabel(r.riskPriority) : '';
  const meta = [category, priority].filter(Boolean).join(' · ');
  return meta ? `${name} (${meta})` : name;
};

const findUserOption = (users, value) => {
  const raw = (value || '').trim();
  if (!raw) return null;
  const byEmail = users.find((u) => (u.email || '').trim().toLowerCase() === raw.toLowerCase());
  if (byEmail) return byEmail;
  const byName = users.find((u) => (u.name || '').trim().toLowerCase() === raw.toLowerCase());
  if (byName) return byName;
  const byLabel = users.find((u) => userLabel(u).toLowerCase() === raw.toLowerCase());
  if (byLabel) return byLabel;
  return { id: `legacy-${raw}`, name: raw, email: '' };
};

const mapRegisterToForm = (register, prev = {}) => ({
  ...prev,
  riskRegisterId: register?.id ?? null,
  riskCategory: register?.riskCategory ?? '',
  resourcesImpacted: register?.resourcesImpacted ?? '',
  riskName: register?.riskName ?? '',
  riskPriority: register?.riskPriority ?? '',
  riskDescription: register?.riskDescription ?? '',
  riskOwner: register?.riskOwner ?? '',
  impact: register?.impact ?? '',
  causes: register?.causes ?? '',
  controlsAndMitigation: register?.controlsAndMitigation ?? '',
  status: register?.status ?? 'OPEN',
});

/**
 * Dialog to add / edit a software risk on the Risk & Impact Assessment stage.
 * When `selectFromRegister` is true, user can pick an existing Risk Register entry.
 */
export default function SoftwareRiskDialog({
  open,
  initialValues,
  onClose,
  onSave,
  selectFromRegister = false,
  excludeRiskRegisterIds = [],
}) {
  const [values, setValues] = useState(emptySoftwareRiskRow);
  const [errors, setErrors] = useState({});
  const [tenantUsers, setTenantUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [registerOptions, setRegisterOptions] = useState([]);
  const [loadingRegisters, setLoadingRegisters] = useState(false);

  const excludedIds = useMemo(
    () => new Set((excludeRiskRegisterIds || []).map((id) => String(id)).filter(Boolean)),
    [excludeRiskRegisterIds]
  );

  const loadTenantUsers = useCallback(async () => {
    const registryTenantId = getRegistryTenantIdFromSession();
    if (!registryTenantId) {
      setTenantUsers([]);
      return;
    }
    setLoadingUsers(true);
    try {
      const list = await fetchActiveTenantUsers(registryTenantId, { page: 0, size: 500 });
      setTenantUsers(list);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load tenant users';
      toast.error(typeof msg === 'string' ? msg : 'Failed to load users');
      setTenantUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadRiskRegisters = useCallback(async () => {
    if (!selectFromRegister) {
      setRegisterOptions([]);
      return;
    }
    setLoadingRegisters(true);
    try {
      const response = await riskRegisterApi.list(listStatusParams(false));
      setRegisterOptions(unwrapList(response));
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to load risk register.'));
      setRegisterOptions([]);
    } finally {
      setLoadingRegisters(false);
    }
  }, [selectFromRegister]);

  useEffect(() => {
    if (!open) return;
    setValues({ ...emptySoftwareRiskRow(), ...(initialValues || {}) });
    setErrors({});
    loadTenantUsers();
    loadRiskRegisters();
  }, [open, initialValues, loadTenantUsers, loadRiskRegisters]);

  const setField = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const availableRegisters = useMemo(() => {
    const currentId =
      values.riskRegisterId != null ? String(values.riskRegisterId) : null;
    return registerOptions.filter((r) => {
      if (r?.id == null) return false;
      const id = String(r.id);
      if (currentId && id === currentId) return true;
      return !excludedIds.has(id);
    });
  }, [registerOptions, excludedIds, values.riskRegisterId]);

  const selectedRegister =
    values.riskRegisterId != null
      ? availableRegisters.find((r) => String(r.id) === String(values.riskRegisterId))
        || registerOptions.find((r) => String(r.id) === String(values.riskRegisterId))
        || null
      : null;

  const linkedFromRegister = Boolean(selectFromRegister && values.riskRegisterId);

  const validate = () => {
    const next = {};
    if (selectFromRegister && !values.riskRegisterId) {
      next.riskRegisterId = 'Select a risk from the risk register';
    }
    if (!(values.riskName || '').trim()) next.riskName = 'Risk name is required';
    if (!(values.riskCategory || '').trim()) next.riskCategory = 'Risk category is required';
    if (!(values.riskPriority || '').trim()) next.riskPriority = 'Risk priority is required';
    if (!(values.status || '').trim()) next.status = 'Status is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave?.({
      ...values,
      riskRegisterId: values.riskRegisterId ?? null,
      riskName: values.riskName.trim(),
      riskCategory: values.riskCategory.trim(),
      resourcesImpacted: (values.resourcesImpacted || '').trim(),
      riskDescription: (values.riskDescription || '').trim(),
      riskOwner: (values.riskOwner || '').trim(),
      impact: (values.impact || '').trim(),
      causes: (values.causes || '').trim(),
      controlsAndMitigation: (values.controlsAndMitigation || '').trim(),
    });
  };

  const field = (name, label, extra = {}) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={values[name] ?? ''}
      onChange={(e) => setField(name, e.target.value)}
      error={Boolean(errors[name])}
      helperText={errors[name] || ''}
      InputProps={{
        readOnly: linkedFromRegister && name !== 'riskDescription',
        ...extra.InputProps,
      }}
      {...extra}
    />
  );

  const selectField = (name, label, options) => (
    <SearchableSelect
      size="small"
      label={label}
      value={values[name] ?? ''}
      onChange={(next) => setField(name, next)}
      error={Boolean(errors[name])}
      helperText={errors[name] || ''}
      disabled={linkedFromRegister}
      options={options.map((opt) => ({ value: opt, label: formatEnumLabel(opt) }))}
    />
  );

  const ownerOption = findUserOption(tenantUsers, values.riskOwner);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {values.id ? 'Edit risk' : 'Add risk'}
      </DialogTitle>
      <DialogContent dividers>
        {selectFromRegister ? (
          <FormSection title="Select from risk register">
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  size="small"
                  options={availableRegisters}
                  loading={loadingRegisters}
                  filterOptions={riskRegisterFilter}
                  value={selectedRegister}
                  onChange={(_, option) => {
                    if (!option) {
                      setValues((prev) => ({
                        ...emptySoftwareRiskRow(),
                        key: prev.key,
                        id: prev.id,
                      }));
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.riskRegisterId;
                        return next;
                      });
                      return;
                    }
                    setValues((prev) => mapRegisterToForm(option, {
                      key: prev.key,
                      id: prev.id,
                    }));
                    setErrors({});
                  }}
                  getOptionLabel={(option) => riskRegisterLabel(option)}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Risk register entry"
                      placeholder="Select a registered risk…"
                      error={Boolean(errors.riskRegisterId)}
                      helperText={
                        errors.riskRegisterId
                        || 'Choose a risk already recorded in the Risk Register'
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingRegisters ? (
                              <CircularProgress color="inherit" size={14} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </FormSection>
        ) : null}

        <FormSection title="Risk details">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              {selectField('riskCategory', 'Risk category', SOFTWARE_RISK_CATEGORY)}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {field('resourcesImpacted', 'Resources impacted')}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {field('riskName', 'Risk name', { required: true })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {selectField('riskPriority', 'Risk priority', SOFTWARE_RISK_PRIORITY)}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                size="small"
                options={tenantUsers}
                loading={loadingUsers}
                filterOptions={userFilter}
                value={ownerOption}
                disabled={linkedFromRegister}
                onChange={(_, option) =>
                  setField('riskOwner', option?.name || option?.email || '')
                }
                getOptionLabel={(option) => userLabel(option)}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Risk owner"
                    placeholder="Select tenant user…"
                    error={Boolean(errors.riskOwner)}
                    helperText={errors.riskOwner || ''}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers ? <CircularProgress color="inherit" size={14} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {selectField('status', 'Status', SOFTWARE_RISK_STATUS)}
            </Grid>
            <Grid size={{ xs: 12 }}>
              {field('riskDescription', 'Risk description', {
                multiline: true,
                minRows: 3,
                helperText: linkedFromRegister
                  ? 'Saved on this system↔risk link and included in the risk report'
                  : errors.riskDescription || '',
              })}
            </Grid>
          </Grid>
        </FormSection>

        <FormSection title="Impact, causes & controls">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              {field('impact', 'Impact of the risk', { multiline: true, minRows: 3 })}
            </Grid>
            <Grid size={{ xs: 12 }}>
              {field('causes', 'Causes of the risk', { multiline: true, minRows: 3 })}
            </Grid>
            <Grid size={{ xs: 12 }}>
              {field('controlsAndMitigation', 'Controls and mitigation', {
                multiline: true,
                minRows: 3,
              })}
            </Grid>
          </Grid>
        </FormSection>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Save risk
        </Button>
      </DialogActions>
    </Dialog>
  );
}
